import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Table, Modal } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';

// Quill modules to handle images and rich text
const quillModules = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    ['link', 'image'],
    ['clean'],
  ],
};

export default function EmailTemplates() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/templates');
      setTemplates(response.data);
      setDataLoaded(true);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleShowModal = (isEdit, template = {}) => {
    setIsEditing(isEdit);
    setTemplateName(isEdit ? template.name : '');
    setTemplateContent(isEdit ? template.content : '');
    setSelectedTemplateId(isEdit ? template.id : null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTemplateName('');
    setTemplateContent('');
    setSelectedTemplateId(null);
  };

  const handleSaveTemplate = async () => {
    const templateData = {
      name: templateName,
      content: templateContent,
    };

    try {
      if (isEditing && selectedTemplateId) {
        await axios.put(`/templates/${selectedTemplateId}`, templateData);
      } else {
        await axios.post('/templates', templateData);
      }
      fetchTemplates();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Falha ao salvar o modelo de mensagem.');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Falha ao excluir o modelo de mensagem.');
    }
  };

  if (!dataLoaded) {
    return <Loading />;
  }

  return (
    <Container fluid className="px-lg-4 px-xl-15">
      <Breadcrumbs
        title="Modelos de Mensagem "
        pages={[{ name: 'Modelos de Mensagens', link: '/email-templates' }]}
      />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1>Modelos de Mensagens</h1>
        <Button
          variant="primary"
          onClick={() => handleShowModal(false)}
          className="text-uppercase"
        >
          <FaPlus className="me-1" /> Novo Modelo
        </Button>
      </div>
      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0 template-table">
              <thead>
                <tr>
                  <th className="template-name-col">Nome</th>
                  <th className="template-actions-col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td className="align-middle template-name-col">
                      <div className="text-truncate" title={template.name}>
                        {template.name}
                      </div>
                    </td>
                    <td className="align-middle template-actions-col">
                      <div className="d-flex justify-content-end gap-1 flex-nowrap">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowModal(true, template)}
                          className="text-uppercase flex-shrink-0"
                          title="Editar template"
                        >
                          <FaEdit className="d-none d-sm-inline" />
                          <span className="d-sm-none">Edit</span>
                          <span className="d-none d-md-inline ms-1">
                            Editar
                          </span>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-uppercase flex-shrink-0"
                          title="Excluir template"
                        >
                          <FaTrash className="d-none d-sm-inline" />
                          <span className="d-sm-none">Del</span>
                          <span className="d-none d-md-inline ms-1" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {templates.length === 0 && (
            <div className="text-center py-4 text-muted">
              <p>Nenhum template encontrado.</p>
              <Button
                variant="primary"
                onClick={() => handleShowModal(false)}
                className="text-uppercase"
              >
                <FaPlus /> Criar Primeiro Template
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for adding/editing templates */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Editar Modelo' : 'Novo Modelo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Conteúdo</Form.Label>
              <div className="mb-2">
                <small className="text-muted">
                  Você pode adicionar imagens, links, formatação de texto.
                </small>
              </div>
              <ReactQuill
                value={templateContent}
                onChange={setTemplateContent}
                modules={quillModules}
                style={{
                  minHeight: '200px',
                }}
                placeholder="Digite o conteúdo do template aqui..."
              />
              <div className="mt-3">
                <small className="text-muted">Preview do conteúdo:</small>
                <div
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '15px',
                    minHeight: '100px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '14px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      templateContent ||
                      'Preview aparecerá aqui conforme você digita...',
                  }}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="text-uppercase"
            onClick={handleCloseModal}
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            className="text-uppercase"
            onClick={handleSaveTemplate}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
